<script lang="ts">
  import { escapeHtml } from "@kontextwerk/web-sdk/core/security"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { euroInputCents, formatEuroCents } from "../../../../_shared/_logic/backlog-estimation"
  import type { ImportDraft } from "../../_shared/_logic/import-draft"
  import { MAX_PREPARATION_DURATION_MINUTES } from "../../../../../../_shared/_logic/work-order-creation"
  const { draft, orderTypes }: { draft: ImportDraft; orderTypes: OrderType[] } = $props()
  const data = $derived(draft.state)
  const id = $props.id()
  const example = $derived.by(() => {
    const rate = euroInputCents(data.hourlyRate)

    if (!rate) return null

    const minutes = ((6000n * 12n + BigInt(rate) - 1n) / BigInt(rate)) * 5n

    return `Beispiel: 60,00 € ÷ ${formatEuroCents(rate)}/Std. × 60 → ${minutes} Minuten (aufgerundet).`
  })
  const options = $derived(
    orderTypes.map((item) => ({ label: item.internal_label, value: item.id })),
  )
  const toggleKey = (key: string, checked: boolean) => {
    data.selectedKeys = checked
      ? [...data.selectedKeys, key]
      : data.selectedKeys.filter((item) => item !== key)
  }
</script>

<div class="work-step form light">
    {#if draft.automaticAvailable()}
        <Input
      id={id + "-automatic"}
      type="checkbox"
      placeholder="Auftragstypen aus der Liste übernehmen"
      bind:value={data.automatic}
      surfaceTone="light"
      brightBackground={true}
      disabled={data.submitting}
    />
    {/if}
    {#if data.automatic}
        <fieldset disabled={data.submitting}>
            <legend>Welche Arbeiten möchten Sie einplanen?</legend>
            {#each data.inspection?.detected_work_types ?? [] as item (item.key)}
                {@const type = orderTypes.find((type) => type.key === item.key)}
                {@const label = type?.internal_label ?? "System-Auftragstyp fehlt im Katalog"}
                <div class="work-option">
                    <Input
            id={id + "-" + item.key}
            type="checkbox"
            placeholder={escapeHtml(label)}
            {label}
            bind:value={
              () => data.selectedKeys.includes(item.key),
              (checked) => toggleKey(item.key, Boolean(checked))
            }
            surfaceTone="light"
            brightBackground={true}
            disabled={data.submitting}
          />
                    <span class="count">{item.row_count} {item.row_count === 1 ? "Eintrag" : "Einträge"}</span
          >
                </div>
            {/each}
        </fieldset>
        {#if !data.selectedKeys.length}<p
        role="status"
        style="color: var(--text-invers-100) !important;"
        >Bitte mindestens eine Arbeitsart auswählen.</p
      >{/if}
    {:else}
        <Input
      id={id + "-types"}
      type="select"
      multiple={true}
      clearable={false}
      placeholder={draft.usesCommonPreset()
        ? "Vorkonfiguration - Auftragstyp"
        : "Auftragstypen wählen"}
      {options}
      bind:value={() => data.selectedIds, draft.selectManualTypes}
      surfaceTone="light"
      brightBackground={true}
      disabled={data.submitting}
      tooltip="Diese Auftragstypen werden für die neu übernommenen Zeilen vorgegeben. Sie können sie anschließend je Zeile ändern."
    />
    {/if}
    {#if draft.usesCommonPreset()}
      <Input
      id={id + "-duration"}
      type="number"
      placeholder="Vorkonfiguration - Dauer"
      bind:value={draft.presetDuration, draft.setPresetDuration}
      min={5}
      max={MAX_PREPARATION_DURATION_MINUTES}
      step={5}
      surfaceTone="light"
      brightBackground={true}
      disabled={data.submitting}
      tooltip="Dauer in Minuten. Diese Vorgabe gilt für die neu übernommenen Zeilen und kann dort einzeln geändert werden."
    />
    {:else}
    <fieldset class="duration-choice" disabled={data.submitting}>
        <legend>Dauer ermitteln</legend>
        {#if draft.priceAvailable()}
            <label class="duration-radio">
                <input
            type="radio"
            name={id + "-method"}
            value="price"
            bind:group={data.durationMethod}
          />
                <span>Aus Preis und Stundensatz</span>
            </label>
            <label class="duration-radio">
                <input
            type="radio"
            name={id + "-method"}
            value="typical"
            bind:group={data.durationMethod}
          />
                <span>Typische Dauer der Auftragstypen</span>
            </label>
        {:else}
            <p style:color|important="var(--text-invers-100)">Typische Dauer der Auftragstypen</p>
            {#if data.inspection?.price_supported}<p
            class="note"
            style:color|important="var(--text-invers-100)"
          >
                    In dieser Datei sind keine lesbaren Preise vorhanden.
                </p>{/if}
        {/if}
        {#if data.durationMethod === "price"}
            <div class="price-fields">
                <Input
            id={id + "-rate"}
            type="text"
            placeholder="Stundensatz (€ / Stunde)"
            bind:value={data.hourlyRate}
            surfaceTone="light"
            brightBackground={true}
            disabled={data.submitting}
            tooltip="Gilt für diese Sammelplanung. Verwenden Sie dieselbe Preisbasis wie in der Liste, beispielsweise einschließlich Umsatzsteuer, falls die Listenbeträge Umsatzsteuer enthalten."
          />
                <Input
            id={id + "-threshold"}
            type="text"
            placeholder="Preisberechnung ab (€)"
            bind:value={data.minimumPrice}
            surfaceTone="light"
            brightBackground={true}
            disabled={data.submitting}
            tooltip="Die Grenze gilt für die Summe der ausgewählten Arbeiten einer Planungszeile. Bei einem kleineren, fehlenden oder nicht aufteilbaren Preis verwenden wir die typischen Dauern."
          />
            </div>
            {#if example}<p class="example" style="color: var(--text-invers-100) !important;"
            >{example}</p
          >{/if}
        {/if}
    </fieldset>
    {/if}
</div>

<style>
  .work-step {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1.2rem;
    padding: 0;
    color: var(--text-invers-100) !important;
  }
  .duration-choice {
    border-top: 1px solid color-mix(in srgb, var(--text-invers-100) 20%, transparent);
    padding-top: 1rem;
  }
  .work-step .duration-radio {
    display: flex !important;
    flex-direction: row !important;
    justify-content: flex-start !important;
    align-items: center !important;
    gap: 0.6rem;
    min-height: 2.6rem;
    height: auto !important;
    padding: 0 !important;
    margin: 0 !important;
    color: var(--text-invers-100) !important;
    cursor: pointer;
  }
  .work-step .duration-radio > input {
    appearance: auto !important;
    width: 18px !important;
    min-width: 18px !important;
    max-width: 18px !important;
    height: 18px !important;
    flex: 0 0 18px !important;
    margin: 0 !important;
    padding: 0 !important;
    color: var(--text-invers-100) !important;
    accent-color: var(--kontext-accent, #821e24);
  }
  .work-step .duration-radio > span {
    color: var(--text-invers-100) !important;
    opacity: 1 !important;
    text-transform: none !important;
    height: auto !important;
    font-weight: 400 !important;
    font-size: 1rem !important;
    line-height: 1.4 !important;
    padding: 0 !important;
    width: auto !important;
    min-width: 0;
    flex: 1;
  }
  .price-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin-top: 0.75rem;
  }
  .note,
  .example {
    font-size: 0.875rem;
    margin-top: 0.65rem;
    line-height: 1.5;
  }
  .work-step.form.light .example {
    color: var(--text-invers-100) !important;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
  }
  legend {
    color: var(--text-invers-100) !important;
    font-weight: 700;
    margin-bottom: 0.75rem;
  }
  .work-option {
    display: flex;
    gap: 0.7rem;
    align-items: center;
    min-height: 2.8rem;
    color: var(--text-invers-100) !important;
  }
  .work-option > span {
    color: var(--text-invers-100) !important;
  }
  .work-option {
    border-bottom: 1px solid color-mix(in srgb, var(--text-invers-100) 20%, transparent);
  }
  .work-option :global(label.checkbox) {
    flex: 1;
    min-width: 0;
  }
  .work-step :global(label.checkbox .checkbox-label),
  .work-step :global(label.checkbox .checkbox-label .kw-safe-html),
  .work-step :global(.labelspan),
  .work-step :global(input) {
    color: var(--text-invers-100) !important;
  }
  .work-option > span {
    overflow-wrap: anywhere;
  }
  .count {
    margin-left: auto;
    white-space: nowrap;
    font-size: 0.85rem;
  }
  p {
    margin: 0;
    color: var(--text-invers-100) !important;
  }
  @media (max-width: 480px) {
    .price-fields {
      grid-template-columns: minmax(0, 1fr);
    }
    .work-option {
      flex-wrap: wrap;
      padding: 0.4rem 0;
    }
    .count {
      width: 100%;
      padding-left: 1.8rem;
    }
  }
</style>
